/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useCallback, useMemo } from 'react';
import type { TaxonomyCodeDto } from '@shared/types';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { useFormulaListStore } from '../../stores/formulaListStore';
import { TaxonomyBrowser } from '../assignment/TaxonomyBrowser';
import { CurrentAssignments } from '../assignment/CurrentAssignments';
import { Tooltip } from '../layout/Tooltip';

interface FormulaDetailModalProps {
  setId: string;
  onClose: () => void;
  /** When true (batch mode), all action buttons are disabled — view only */
  readOnly?: boolean;
}

export function FormulaDetailModal({ setId, onClose, readOnly = false }: FormulaDetailModalProps) {
  const {
    formula, isLoading, error, isSaving, saveError, successMessage,
    loadFormula, assign, remove, clearSuccess,
  } = useAssignmentStore();
  const { formulas } = useFormulaListStore();

  // Set of already-assigned taxonomy set IDs to exclude from browser
  const assignedTaxonomySetIds = useMemo(
    () => new Set((formula?.activeAssignments ?? []).map((a) => a.taxonomyCode.setId)),
    [formula?.activeAssignments]
  );

  useEffect(() => {
    loadFormula(setId);
  }, [setId, loadFormula]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(clearSuccess, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, clearSuccess]);

  const handleSelect = useCallback(async (taxonomy: TaxonomyCodeDto) => {
    if (!formula) return;
    await assign(formula.setId, taxonomy.setId);
  }, [formula, assign]);

  const handleRemove = useCallback(async (xlinkSetId: string) => {
    if (confirm('Remove this taxonomy assignment?')) {
      await remove(xlinkSetId, 'Manually removed by pharmacist');
    }
  }, [remove]);

  // Find prev/next within current page — use loaded formula's setId (not prop) to track position after navigation
  const currentSetId = formula?.setId ?? setId;
  const currentIdx = formulas.findIndex((f) => f.setId === currentSetId);
  const prevFormula = currentIdx > 0 ? formulas[currentIdx - 1] : null;
  const nextFormula = currentIdx >= 0 && currentIdx < formulas.length - 1 ? formulas[currentIdx + 1] : null;

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.altKey && e.key === 'ArrowLeft' && prevFormula) loadFormula(prevFormula.setId);
      else if (e.altKey && e.key === 'ArrowRight' && nextFormula) loadFormula(nextFormula.setId);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, prevFormula, nextFormula, loadFormula]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 py-3" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: nav + title */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shrink-0">
          <Tooltip text="Previous formula in the list (Alt+←)">
            <button
              onClick={() => prevFormula && loadFormula(prevFormula.setId)}
              disabled={!prevFormula}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200"
              title="Alt+←"
            >
              ← Prev
            </button>
          </Tooltip>

          <div className="flex items-center gap-2 min-w-0 mx-3">
            <h2 className="text-base font-bold text-gray-900 truncate">
              {isLoading ? 'Loading...' : formula?.name}
            </h2>
            {currentIdx >= 0 && (
              <span className="text-xs text-gray-400 shrink-0">{currentIdx + 1}/{formulas.length}</span>
            )}
            {readOnly && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium shrink-0">
                View Only
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Tooltip text="Next formula in the list (Alt+→)">
              <button
                onClick={() => nextFormula && loadFormula(nextFormula.setId)}
                disabled={!nextFormula}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200"
                title="Alt+→"
              >
                Next →
              </button>
            </Tooltip>
            <Tooltip text="Close modal (Esc)">
              <button onClick={onClose} className="ml-1 text-gray-400 hover:text-gray-600 text-lg leading-none px-1.5" title="Esc">✕</button>
            </Tooltip>
          </div>
        </div>

        {/* Body: fills remaining height */}
        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col min-h-0">
          {isLoading && !formula && <div className="text-center text-gray-400 py-8">Loading formula...</div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          {formula && (
            <>
              {/* Top section: left (formula props + assignments) | right (ingredients) */}
              <div className="flex gap-3 mb-3 shrink-0 max-h-48">
                {/* Left column: formula properties + current assignments stacked */}
                <div className="flex-1 min-w-0 flex flex-col gap-2 min-h-0">
                  {/* Formula properties */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      {formula.genericName && <div className="w-full text-gray-500 text-sm mb-1">{formula.genericName}</div>}
                      <div><span className="text-gray-400">Form:</span> <span className="font-medium">{formula.dosageForm || '—'}</span></div>
                      <div><span className="text-gray-400">Strength:</span> <span className="font-medium">{formula.strength || '—'}</span></div>
                      <div><span className="text-gray-400">Schedule:</span> <span className="font-medium">{formula.schedule || '—'}</span></div>
                      <div><span className="text-gray-400">Routes:</span> <span className="font-medium">{formula.routes?.replace(/[{}"]/g, '') || '—'}</span></div>
                      <div><span className="text-gray-400">Source:</span> <span className="font-medium">{formula.formulaSourceName || '—'}</span></div>
                      <div><span className="text-gray-400">Sterile:</span> <span className="font-medium">{formula.sterile ? 'Yes' : 'No'}</span></div>
                    </div>
                  </div>

                  {/* Current assignments — grows to fill remaining height */}
                  <div className="flex-1 min-h-0">
                    <CurrentAssignments
                      assignments={formula.activeAssignments ?? []}
                      onRemove={handleRemove}
                      disabled={readOnly}
                    />
                  </div>
                </div>

                {/* Right column: ingredients with scroll */}
                {formula.ingredients && formula.ingredients.length > 0 && (
                  <div className="w-56 shrink-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 overflow-y-auto">
                    <h4 className="text-xs font-medium text-gray-500 mb-1">Ingredients</h4>
                    <div className="flex flex-wrap gap-1">
                      {formula.ingredients.map((ing, idx) => (
                        <span key={`${ing.setId}-${idx}`} className="inline-block px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[11px] font-mono">{ing.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Alerts */}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2 mb-2 text-sm shrink-0">{successMessage}</div>
              )}
              {saveError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-2 text-sm shrink-0">{saveError}</div>
              )}

              {/* Taxonomy browser — fills remaining space */}
              <div className="flex-1 min-h-0 flex flex-col bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <h4 className="text-sm font-medium text-gray-700">
                    Assign Taxonomy Code
                  </h4>
                </div>
                <div className="flex-1 min-h-0">
                  <TaxonomyBrowser onSelect={handleSelect} formulaSetId={formula.setId} isSaving={isSaving} fillHeight excludeSetIds={assignedTaxonomySetIds} disabled={readOnly} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
